import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { ResolveConflictDialogComponent, ResolveConflictDialogResult } from './resolve-conflict-dialog/resolve-conflict-dialog.component';
import { DriverDeviationService } from '../../services/driver-deviation.service';
import { TrainDeviation } from '../../models/train-deviation.model';
import { DriverDeviationFilter } from '../../models/driver-deviation-filter.model';
import { ResolveDeviationRequest, DriverDeviationSelection } from '../../models/resolve-deviation-request.model';
import { ChipFilterGroup } from '../../shared/chip-filter/chip-filter-group';
import { ConflictGroup } from '../../models/conflict-group.model';

type TabFilter = 'new' | 'handled';

@Component({
  selector: 'app-driver-deviations',
  standalone: false,
  templateUrl: './driver-deviations.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./driver-deviations.component.scss']
})
export class DriverDeviationsComponent implements OnInit, OnDestroy {
  // Data
  trainDeviations: TrainDeviation[] = [];
  filteredDeviations: TrainDeviation[] = [];
  paginatedDeviations: TrainDeviation[] = [];

  // UI state
  activeTab: TabFilter = 'new';
  selectedDate: Date | null = new Date();
  loading: boolean = false;

  // Chip filters
  filters = new ChipFilterGroup<TrainDeviation>([
    {
      key: 'train',
      extract: d => d.trainNumber != null ? [String(d.trainNumber)] : [],
      sort: (a, b) => a.localeCompare(b, 'sv-SE', { numeric: true })
    },
    {
      key: 'driver',
      extract: d => d.allDrivers
        .filter(dr => !d.isHandled || dr.isSelected)
        .map(dr => `${dr.firstName} ${dr.lastName}`.trim())
        .filter(Boolean)
    },
    {
      key: 'route',
      extract: d => d.route?.origin != null && d.route?.destination != null
        ? [`${d.route.origin} → ${d.route.destination}`]
        : []
    }
  ]);

  // Pagination
  pageSize: number = 10;
  pageIndex: number = 0;
  pageSizeOptions: number[] = [10, 25, 50];

  // Sorting
  sortColumn: 'train' | 'time' | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  // Cleanup
  private destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private deviationService: DriverDeviationService
  ) {}

  ngOnInit() {
    this.loadData();

    this.deviationService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.loading = loading;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData() {
    const selectedDate = this.selectedDate;
    if (!selectedDate) {
      this.snackBar.open('Välj ett trafikdygn', 'OK', { duration: 3000 });
      return;
    }

    this.loading = true;
    this.filters.clear();

    const trafficDate = new Date(selectedDate);
    trafficDate.setHours(0, 0, 0, 0);

    const filter = new DriverDeviationFilter(
      true,
      undefined,
      trafficDate
    );

    this.deviationService.getDeviations(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (deviations) => {
          this.trainDeviations = deviations;
          this.filters.populate(this.getTabDeviations());
          this.applyFilters();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading driver deviations:', error);
          this.snackBar.open(
            'Fel vid hämtning av föraravvikelser',
            'Stäng',
            {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              panelClass: ['error-snackbar']
            }
          );
          this.loading = false;
        }
      });
  }

  applyFilters() {
    let filtered = this.trainDeviations.filter(deviation =>
      this.activeTab === 'new' ? !deviation.isHandled : deviation.isHandled
    );
    filtered = this.filters.apply(filtered);
    this.filteredDeviations = filtered;
    this.pageIndex = 0;

    if (this.sortColumn) {
      this.applySorting();
    } else {
      this.updatePagination();
    }
  }

  clearFilters() {
    this.filters.clear();
    this.pageIndex = 0;
    this.applyFilters();
  }

  sortBy(column: 'train' | 'time') {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySorting();
  }

  applySorting() {
    if (!this.sortColumn) {
      this.updatePagination();
      return;
    }

    this.filteredDeviations = [...this.filteredDeviations].sort((a, b) => {
      let comparison = 0;

      switch (this.sortColumn) {
        case 'train': {
          const trainA = (a.trainNumber || '').toString();
          const trainB = (b.trainNumber || '').toString();
          comparison = trainA.localeCompare(trainB, 'sv-SE', { numeric: true });
          break;
        }
        case 'time': {
          const timeA = a.route?.departureAtUtc ? new Date(a.route.departureAtUtc).getTime() : 0;
          const timeB = b.route?.departureAtUtc ? new Date(b.route.departureAtUtc).getTime() : 0;
          comparison = timeA - timeB;
          break;
        }
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    this.updatePagination();
  }

  updatePagination() {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedDeviations = this.filteredDeviations.slice(startIndex, endIndex);
  }

  onTabChange(tab: TabFilter) {
    this.activeTab = tab;
    this.pageIndex = 0;
    this.filters.populate(this.getTabDeviations());
    this.applyFilters();
  }

  private getTabDeviations(): TrainDeviation[] {
    return this.trainDeviations.filter(d =>
      this.activeTab === 'new' ? !d.isHandled : d.isHandled
    );
  }

  getDisplayedDrivers(deviation: TrainDeviation) {
    if (!deviation.isHandled) return deviation.allDrivers;
    return deviation.allDrivers.filter(d => d.isSelected);
  }

  getDisplayedGroupDrivers(group: ConflictGroup, isHandled: boolean) {
    if (!isHandled) return group.trainDrivers;
    return group.trainDrivers.filter(driver => driver.isSelected);
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePagination();
  }

  getSortIcon(column: 'train' | 'time'): string {
    if (this.sortColumn !== column) {
      return 'unfold_more';
    }
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  getNewCount(): number {
    return this.trainDeviations.filter(d => !d.isHandled).length;
  }

  getHandledCount(): number {
    return this.trainDeviations.filter(d => d.isHandled).length;
  }

  openResolveDialog(deviation: TrainDeviation) {
    const dialogRef = this.dialog.open<ResolveConflictDialogComponent, TrainDeviation, ResolveConflictDialogResult>(
      ResolveConflictDialogComponent,
      {
        width: '600px',
        maxWidth: '90vw',
        data: deviation,
        panelClass: ['resolve-conflict-dialog'],
        disableClose: false
      }
    );

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'save') {
        this.handleDriverSelections(deviation, result.selections, result.comment);
      }
    });
  }

  handleDriverSelections(deviation: TrainDeviation, selections: DriverDeviationSelection[], comment: string) {
    this.deviationService.resolveDeviation(
      deviation.id,
      new ResolveDeviationRequest(selections, comment)
    ).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadData();
          this.snackBar.open(
            'Avvikelse hanterad',
            'Stäng',
            {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              panelClass: ['success-snackbar']
            }
          );
        },
        error: (error) => {
          console.error('Error resolving deviation:', error);
          this.snackBar.open(
            'Fel vid hantering av avvikelse',
            'Stäng',
            {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              panelClass: ['error-snackbar']
            }
          );
        }
      });
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'NEW_TRAIN': return 'Nytt tåg';
      case 'CONFLICT': return 'Konflikt';
      case 'DEVIATION_HANDLED': return 'Avvikelse hanterad';
      default: return status;
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'NEW_TRAIN': return 'status-new-train';
      case 'CONFLICT': return 'status-conflict';
      case 'DEVIATION_HANDLED': return 'status-handled';
      default: return '';
    }
  }

  copyPhoneNumber(phoneNumber: string, event: Event) {
    event.stopPropagation();
    navigator.clipboard.writeText(phoneNumber).then(() => {
      this.snackBar.open(
        `Telefonnummer kopierat: ${phoneNumber}`,
        'Stäng',
        {
          duration: 2000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['info-snackbar']
        }
      );
    });
  }

  getRoleLabel(role: string): string {
    switch (role) {
      case 'PLANNED': return 'Planerad';
      case 'DEVIATING': return 'Avvikande';
      case 'NOT_SELECTED': return 'Ej vald';
      default: return role;
    }
  }

  getRoleClass(role: string): string {
    switch (role) {
      case 'PLANNED': return 'role-planned';
      case 'DEVIATING': return 'role-deviating';
      default: return '';
    }
  }

  formatDate(date: Date): string {
    return date.toLocaleDateString('sv-SE');
  }

  formatTime(date: Date): string {
    return date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
  }

  formatDateTime(date: Date): string {
    return `${date.toLocaleDateString('sv-SE')} ${date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}`;
  }
}
